import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { copiaDoFicheiro } from "../../lib/copiaDoFicheiro";
import { mensagemDeErro } from "../../lib/erroApi";
import { Plus, ArrowLeft, Pencil, Power, UserPlus, Shield, AlertCircle, BarChart3, TrendingUp, Activity, Trophy, School } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth, API } from "../../App";

const sidColor = (s) => s?.primary_color || "#f97316";

const slugify = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

export default function SuperAdminSchools() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState({ total: 0, items: [] });

  const isSuper = user?.role === "super_admin";

  const fetchSchools = async () => {
    try {
      const r = await axios.get(`${API}/super-admin/schools`, { withCredentials: true });
      setSchools(r.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar escolas");
    }
  };

  const fetchAudit = async () => {
    try {
      const r = await axios.get(`${API}/super-admin/audit?limit=50`, { withCredentials: true });
      setAudit(r.data || { total: 0, items: [] });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (!isSuper) {
      navigate("/admin", { replace: true });
      return;
    }
    Promise.all([fetchSchools(), fetchAudit()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSuper]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cockpit-bg text-slate-400">
        A carregar painel super-admin…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg">
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin")}
              data-testid="back-to-admin"
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Admin
            </Button>
            <div className="h-5 w-px bg-slate-700" />
            <Shield className="w-4 h-4 text-purple-400" />
            <h1 className="font-chivo text-lg text-cockpit-text-bright">Super-Admin · Escolas</h1>
          </div>
          <CreateSchoolButton onCreated={fetchSchools} />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <Tabs defaultValue="schools" className="space-y-5">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="schools" data-testid="tab-schools">
              Escolas ({schools.length})
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              Auditoria · Cross-tenant ({audit.total})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schools" className="space-y-4">
            {schools.length === 0 ? (
              <p className="text-slate-500 text-sm">Sem escolas. Cria a primeira no botão "+".</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {schools.map((s) => (
                  <SchoolCard key={s.school_id} school={s} onChanged={fetchSchools} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="audit">
            <AuditView audit={audit} onRefresh={fetchAudit} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// School card
// ────────────────────────────────────────────────────────────────────
function SchoolCard({ school, onChanged }) {
  return (
    <Card
      data-testid={`school-${school.school_id}`}
      className="instrument-panel relative overflow-hidden"
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: sidColor(school) }}
      />
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base text-cockpit-text-bright leading-tight">{school.name}</CardTitle>
            <p className="text-[11px] text-slate-500 mt-0.5 font-mono">{school.slug}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {school.school_id === "sch_default" && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/40"
                title="Escola por defeito do sistema — não pode ser eliminada"
                data-testid={`default-badge-${school.school_id}`}
              >
                Default
              </span>
            )}
            {!school.is_active && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40"
                data-testid={`inactive-badge-${school.school_id}`}
              >
                Inativa
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <Stat label="Alunos" value={school.student_count} testid={`stat-students-${school.school_id}`} />
          <Stat label="Instrutores" value={school.instructor_count} testid={`stat-instructors-${school.school_id}`} />
          <Stat label="Turmas" value={school.class_count} testid={`stat-classes-${school.school_id}`} />
        </div>
        <div className="flex flex-wrap gap-2">
          <EditSchoolButton school={school} onUpdated={onChanged} />
          <SchoolMetricsButton school={school} />
          <InviteInstructorButton school={school} />
          {school.school_id !== "sch_default" && (
            school.is_active ? (
              <DeactivateSchoolButton school={school} onChanged={onChanged} />
            ) : (
              <ReactivateSchoolButton school={school} onChanged={onChanged} />
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="rounded bg-slate-900/60 border border-slate-800 px-2 py-1.5">
    <div className="font-mono text-lg text-cyan-300" data-testid={testid}>{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
  </div>
);

// ────────────────────────────────────────────────────────────────────
// Create school
// ────────────────────────────────────────────────────────────────────
function CreateSchoolButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", primary_color: "#f97316", product: "both" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/super-admin/schools`, {
        name: form.name.trim(),
        slug: form.slug.trim() || slugify(form.name),
        primary_color: form.primary_color,
        product: form.product,
      }, { withCredentials: true });
      toast.success("Escola criada");
      setOpen(false);
      setForm({ name: "", slug: "", primary_color: "#f97316", product: "both" });
      onCreated?.();
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro ao criar escola"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-school-btn" className="btn-cockpit">
          <Plus className="w-4 h-4 mr-1" /> Nova Escola
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-950 border-slate-800">
        <DialogHeader>
          <DialogTitle>Criar Escola</DialogTitle>
          <DialogDescription>Cada escola é um tenant isolado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nome</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
              placeholder="Ex: Aero Clube de Lisboa"
              data-testid="school-name-input"
            />
          </div>
          <div>
            <Label className="text-xs">Slug (URL-safe)</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
              placeholder="aero-clube-lisboa"
              data-testid="school-slug-input"
              className="font-mono"
            />
          </div>
          <div>
            <Label className="text-xs">Cor primária</Label>
            <div className="flex items-center gap-2">
              <Input
                type="color"
                value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="w-14 h-10 p-1"
                data-testid="school-color-input"
              />
              <span className="text-xs text-slate-500 font-mono">{form.primary_color}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs">Produto</Label>
            <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
              <SelectTrigger data-testid="school-product-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Ambos (Treino + DI)</SelectItem>
                <SelectItem value="training">Só Treino</SelectItem>
                <SelectItem value="fleet">Só DI (gestão de frota)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="btn-secondary-cockpit">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={!form.name.trim() || busy}
            data-testid="confirm-create-school"
            className="btn-cockpit"
          >
            {busy ? "A criar..." : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Edit school
// ────────────────────────────────────────────────────────────────────
function EditSchoolButton({ school, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: school.name,
    slug: school.slug,
    primary_color: school.primary_color || "#f97316",
    product: school.product || "both",
  });
  const [busy, setBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState(school.logo_url || null);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/super-admin/schools/${school.school_id}`, form, { withCredentials: true });
      toast.success("Escola atualizada");
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro ao atualizar"));
    } finally {
      setBusy(false);
    }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Ficheiro tem de ser uma imagem");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx 2MB)");
      return;
    }
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await copiaDoFicheiro(file));
      const r = await axios.post(
        `${API}/super-admin/schools/${school.school_id}/logo`,
        fd,
        { withCredentials: true },
      );
      setLogoUrl(r.data?.logo_url || null);
      toast.success("Logo atualizado");
      onUpdated?.();
    } catch (err) {
      toast.error(mensagemDeErro(err, "Erro ao carregar logo"));
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await axios.delete(
        `${API}/super-admin/schools/${school.school_id}/logo`,
        { withCredentials: true },
      );
      setLogoUrl(null);
      toast.success("Logo removido");
      onUpdated?.();
    } catch (err) {
      toast.error(mensagemDeErro(err, "Erro ao remover logo"));
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="btn-secondary-cockpit"
          data-testid={`edit-school-${school.school_id}`}
        >
          <Pencil className="w-3 h-3 mr-1" /> Editar
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-950 border-slate-800">
        <DialogHeader>
          <DialogTitle>Editar Escola</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Logotipo</Label>
            <div className="flex items-center gap-3 mt-1.5">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={form.name}
                  className="w-16 h-16 rounded object-contain border border-slate-800 bg-slate-900 p-1"
                  data-testid={`logo-preview-${school.school_id}`}
                />
              ) : (
                <div
                  className="w-16 h-16 rounded flex items-center justify-center"
                  style={{ background: form.primary_color }}
                >
                  <School className="w-7 h-7 text-white" />
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileRef}
                  onChange={onLogoFile}
                  className="hidden"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="btn-secondary-cockpit"
                  onClick={() => fileRef.current?.click()}
                  disabled={logoBusy}
                  data-testid={`upload-logo-${school.school_id}`}
                >
                  {logoBusy ? "A carregar..." : logoUrl ? "Substituir logo" : "Carregar logo"}
                </Button>
                {logoUrl && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-400 hover:text-red-300"
                    onClick={removeLogo}
                    disabled={logoBusy}
                    data-testid={`remove-logo-${school.school_id}`}
                  >
                    Remover
                  </Button>
                )}
                <p className="text-[10px] text-slate-500">PNG/JPG/WebP, máx 2MB</p>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Nome</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Slug</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
              className="font-mono"
            />
          </div>
          <div>
            <Label className="text-xs">Cor primária</Label>
            <div className="flex items-center gap-2">
              <Input
                type="color"
                value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="w-14 h-10 p-1"
              />
              <span className="text-xs text-slate-500 font-mono">{form.primary_color}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs">Produto</Label>
            <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
              <SelectTrigger data-testid={`school-product-select-${school.school_id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Ambos (Treino + DI)</SelectItem>
                <SelectItem value="training">Só Treino</SelectItem>
                <SelectItem value="fleet">Só DI (gestão de frota)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="btn-secondary-cockpit">Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="btn-cockpit" data-testid={`save-school-${school.school_id}`}>
            {busy ? "A guardar..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Invite instructor
// ────────────────────────────────────────────────────────────────────
function InviteInstructorButton({ school }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: school.product === "fleet" ? "admin" : "instructor" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/super-admin/schools/${school.school_id}/invite-instructor`,
        form,
        { withCredentials: true },
      );
      setResult(r.data);
      toast.success(r.data.status === "promoted" ? "Utilizador promovido a instrutor" : "Convite criado");
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro ao convidar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="btn-secondary-cockpit"
          data-testid={`invite-instructor-${school.school_id}`}
        >
          <UserPlus className="w-3 h-3 mr-1" /> Convidar Instrutor
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-950 border-slate-800">
        <DialogHeader>
          <DialogTitle>Convidar Instrutor — {school.name}</DialogTitle>
          <DialogDescription>
            Se o email já tiver conta, é promovido. Caso contrário, criamos um placeholder
            que herda o role no primeiro login com Google.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="instrutor@exemplo.pt"
              data-testid="invite-email-input"
            />
          </div>
          <div>
            <Label className="text-xs">Nome (opcional)</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nome do instrutor"
            />
          </div>
          <div>
            <Label className="text-xs">Papel</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instructor">Instrutor</SelectItem>
                <SelectItem value="admin">Administrador (acede ao Aerofleet Center)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {result && (
            <div className="text-xs p-3 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-200" data-testid="invite-result">
              {result.status === "promoted"
                ? `✓ ${result.email} foi promovido a instrutor desta escola.`
                : `✓ Placeholder criado para ${result.email}. Recebe acesso no 1º login com Google.`}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="btn-secondary-cockpit">Fechar</Button>
          <Button
            onClick={submit}
            disabled={!form.email.trim() || busy}
            className="btn-cockpit"
            data-testid="confirm-invite-instructor"
          >
            {busy ? "A enviar..." : "Convidar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// School metrics modal
// ────────────────────────────────────────────────────────────────────
function SchoolMetricsButton({ school }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/super-admin/schools/${school.school_id}/metrics`,
        { withCredentials: true },
      );
      setData(r.data);
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro ao carregar métricas"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) fetchMetrics();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="btn-secondary-cockpit"
          data-testid={`metrics-school-${school.school_id}`}
        >
          <BarChart3 className="w-3 h-3 mr-1" /> Métricas
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="w-4 h-4" style={{ color: sidColor(school) }} />
            Métricas — {school.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            População, prontidão para exame e atividade.
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="text-center py-10 text-slate-500 text-sm" data-testid="metrics-loading">
            A carregar métricas…
          </div>
        ) : (
          <div className="space-y-5" data-testid={`metrics-content-${school.school_id}`}>
            {/* Population */}
            <Section title="População">
              <div className="grid grid-cols-3 gap-2">
                <MetricBox label="Alunos" value={data.population.students} testid="metric-students" />
                <MetricBox label="Instrutores" value={data.population.instructors} testid="metric-instructors" />
                <MetricBox label="Turmas" value={data.population.classes} testid="metric-classes" />
              </div>
            </Section>

            {/* Exames de avaliação */}
            <Section title="Exames de avaliação" icon={<Trophy className="w-3 h-3" />}>
              <div className="grid grid-cols-3 gap-2">
                <MetricBox
                  label="Realizados"
                  value={data.exams?.evaluation_total ?? 0}
                  testid="metric-evaluation-total"
                />
                <MetricBox
                  label="Taxa aprovação"
                  value={data.exams?.pass_rate != null ? `${data.exams.pass_rate}%` : "—"}
                  testid="metric-pass-rate"
                />
                <MetricBox
                  label="Acerto médio"
                  value={data.exams?.avg_accuracy != null ? `${data.exams.avg_accuracy}%` : "—"}
                  testid="metric-avg-accuracy"
                />
              </div>
              <p className="text-[10px] text-slate-600 mt-1.5 leading-tight">
                Apenas exames que contam para avaliação (excluindo simulados de preparação).
              </p>
            </Section>

            {/* Treinos · Prontidão para exame */}
            <Section title="Prontidão para exame" icon={<Activity className="w-3 h-3" />}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MetricBox
                  label="Pronto"
                  value={data.readiness?.ready ?? 0}
                  testid="metric-ready"
                  color="text-emerald-300"
                />
                <MetricBox
                  label="Quase"
                  value={data.readiness?.near ?? 0}
                  testid="metric-near"
                  color="text-amber-300"
                />
                <MetricBox
                  label="Em desenvolvimento"
                  value={data.readiness?.developing ?? 0}
                  testid="metric-developing"
                  color="text-red-300"
                />
                <MetricBox
                  label="Sem dados"
                  value={data.readiness?.no_data ?? 0}
                  testid="metric-no-data"
                  color="text-slate-400"
                />
              </div>
              <p className="text-[10px] text-slate-600 mt-1.5 leading-tight">
                Por aluno: média do acerto em todos os exames de avaliação.
                Pronto ≥ {data.readiness?.pass_threshold || 75}%, Quase {data.readiness?.near_threshold || 60}–{data.readiness?.pass_threshold || 75}%, Em desenvolvimento &lt; {data.readiness?.near_threshold || 60}%.
                Mínimo {data.readiness?.min_exams || 1} exame para classificar.
              </p>
            </Section>

            {/* Atividade */}
            <Section title="Atividade · sessões de treino" icon={<TrendingUp className="w-3 h-3" />}>
              <div className="grid grid-cols-3 gap-2">
                <MetricBox
                  label="7d"
                  value={data.activity.training_sessions_7d}
                  testid="metric-sessions-7d"
                />
                <MetricBox
                  label="30d"
                  value={data.activity.training_sessions_30d}
                  testid="metric-sessions-30d"
                />
                <MetricBox
                  label="Total"
                  value={data.activity.training_sessions_alltime ?? "—"}
                  testid="metric-sessions-alltime"
                />
              </div>
            </Section>

            {/* Top students */}
            <Section title="Top 5 alunos por XP" icon={<TrendingUp className="w-3 h-3" />}>
              {(!data.top_students || data.top_students.length === 0) ? (
                <p className="text-xs text-slate-500 italic">Sem dados de alunos.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 border-b border-slate-800">
                      <tr>
                        <th className="text-left py-1.5 px-2 w-8">#</th>
                        <th className="text-left py-1.5 px-2">Nome</th>
                        <th className="text-right py-1.5 px-2">XP</th>
                        <th className="text-right py-1.5 px-2">Skill</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      {data.top_students.map((s, i) => (
                        <tr
                          key={s.user_id}
                          className="border-b border-slate-900"
                          data-testid={`top-student-${i}`}
                        >
                          <td className="py-1.5 px-2 font-mono text-slate-500">{i + 1}</td>
                          <td className="py-1.5 px-2">
                            <div className="font-medium">{s.name || "—"}</div>
                            <div className="text-[10px] text-slate-500">{s.email}</div>
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono text-cyan-300">{s.xp || 0}</td>
                          <td className="py-1.5 px-2 text-right font-mono text-amber-300">
                            {s.skill_rating ? Math.round(s.skill_rating) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMetrics}
            disabled={loading}
            className="btn-secondary-cockpit"
            data-testid="refresh-metrics"
          >
            Atualizar
          </Button>
          <Button onClick={() => setOpen(false)} className="btn-cockpit">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, icon, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
      {icon}
      {title}
    </div>
    {children}
  </div>
);

const MetricBox = ({ label, value, testid, color }) => (
  <div className="rounded bg-slate-900/60 border border-slate-800 px-2 py-2 text-center">
    <div className={`font-mono text-base sm:text-lg ${color || "text-cyan-300"} truncate`} data-testid={testid}>
      {value ?? 0}
    </div>
    <div className="text-[9px] sm:text-[10px] uppercase tracking-wide text-slate-500 leading-tight">{label}</div>
  </div>
);

// ────────────────────────────────────────────────────────────────────
// Deactivate school
// ────────────────────────────────────────────────────────────────────
function DeactivateSchoolButton({ school, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/super-admin/schools/${school.school_id}`, { withCredentials: true });
      toast.success("Escola desativada");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro ao desativar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="btn-secondary-cockpit text-red-400 hover:text-red-300"
          data-testid={`deactivate-school-${school.school_id}`}
        >
          <Power className="w-3 h-3 mr-1" />
          Desativar
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-950 border-slate-800">
        <DialogHeader>
          <DialogTitle>Desativar {school.name}?</DialogTitle>
          <DialogDescription>
            Os dados são preservados (soft-delete). Os utilizadores desta escola continuam a poder
            entrar — vai ser preciso reativar manualmente para os bloquear ou removê-los.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="btn-secondary-cockpit">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="btn-cockpit"
            data-testid={`confirm-deactivate-${school.school_id}`}
          >
            {busy ? "A desativar..." : "Desativar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Reactivate school
// ────────────────────────────────────────────────────────────────────
function ReactivateSchoolButton({ school, onChanged }) {
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.put(
        `${API}/super-admin/schools/${school.school_id}`,
        { is_active: true },
        { withCredentials: true },
      );
      toast.success("Escola reativada");
      onChanged?.();
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro ao reativar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="btn-secondary-cockpit text-emerald-400 hover:text-emerald-300"
      onClick={submit}
      disabled={busy}
      data-testid={`reactivate-school-${school.school_id}`}
    >
      <Power className="w-3 h-3 mr-1" />
      {busy ? "A reativar..." : "Reativar"}
    </Button>
  );
}

// ────────────────────────────────────────────────────────────────────
// Audit view
// ────────────────────────────────────────────────────────────────────
function AuditView({ audit, onRefresh }) {
  const items = audit.items || [];
  return (
    <Card className="instrument-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
        <div>
          <CardTitle className="text-sm text-cockpit-text-bright">Tentativas de cross-tenant access</CardTitle>
          <p className="text-[11px] text-slate-500">
            Cada linha = um endpoint que retornou 403 por tentativa cross-school.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} className="btn-secondary-cockpit" data-testid="refresh-audit">
          Atualizar
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-40" />
            Sem eventos. (Ótimo sinal — ninguém tentou aceder a outra escola.)
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left py-2 px-2">Quando</th>
                  <th className="text-left py-2 px-2">Quem</th>
                  <th className="text-left py-2 px-2">Recurso</th>
                  <th className="text-left py-2 px-2">Tentou aceder</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-900" data-testid={`audit-row-${i}`}>
                    <td className="py-2 px-2 font-mono text-[11px] text-slate-500">
                      {new Date(it.timestamp).toLocaleString("pt-PT")}
                    </td>
                    <td className="py-2 px-2">
                      <div className="font-medium">{it.user_email}</div>
                      <div className="text-[10px] text-slate-500">{it.user_role} · {it.user_school_id}</div>
                    </td>
                    <td className="py-2 px-2">
                      <span className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] font-mono">{it.resource}</span>
                      <span className="ml-2 text-[10px] text-slate-500">{it.resource_id}</span>
                    </td>
                    <td className="py-2 px-2 font-mono text-[11px] text-red-400">{it.target_school_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
